import type { CSSProperties } from 'react'
import { dataFreshnessLabel, type Period } from '../landing'
import { formatDateTime } from '../format'
import { InfoTip } from './InfoTip'

// Calm, always-honest data-recency line. Renders nothing until periods are loaded.
//
// The old wording — "more recent months fill in as orders complete" — described the
// settlement lag of the order-derived sweep, where a month kept growing for weeks after it
// closed. Heart's daily summaries don't work that way: the night after a day closes it is
// locked to fixed values and never changes again, and a return is booked on the day it is
// processed rather than written back onto the original day (Linus, 18/9). Saying a month
// still fills in would now be untrue, and it is the one line that tells a manufacturer how
// much to trust the newest figure.
export function DataFreshnessNote({ periods, lastIngestedAt }: { periods: Period[]; lastIngestedAt?: string | null }) {
  const label = dataFreshnessLabel(periods)
  if (!label) return null
  const fetched = fetchedLabel(lastIngestedAt)
  return (
    // MPD-64: the date stays visible; how the day closes and when the data was last read go in the ⓘ.
    <div style={S.note} role="note">
      <span style={S.strong}>Data through</span> {label.replace(/^through /, '')}
      <InfoTip label="About data freshness" size="small">
        Sales data closes each day overnight, so today is not included yet. Returns count on the day they
        are processed.{fetched && <> Last updated {fetched}.</>}
      </InfoTip>
    </div>
  )
}

// ISO-stämpel från datalagrets senaste körning → "30 juli 10:46". Tål ogiltig/tom input.
function fetchedLabel(iso?: string | null): string | null {
  if (!iso) return null
  return formatDateTime(iso)
}

const S: Record<string, CSSProperties> = {
  note: {
    margin: '0 0 14px',
    padding: '8px 14px',
    borderRadius: 8,
    background: 'var(--brand-tint, #eef1f0)',
    color: 'var(--muted, #4a4a4a)',
    fontSize: 12.5,
    lineHeight: 1.5,
  },
  strong: { fontWeight: 700, color: 'var(--brand-dark)' },
}
